import BaseController from "niteroi/packages/system/controllers/Base.controller";
import JSONModel from "sap/ui/model/json/JSONModel";
import {ListItemBase$PressEvent} from "sap/m/ListItemBase";
import {Button$PressEvent} from "sap/m/Button";
import Toolbar from "sap/m/Toolbar";
import Page from "sap/m/Page";
import {ICompany, ILoadDataResponse} from "niteroi/packages/system/models/interfaces";
import HeaderContainer from "sap/m/HeaderContainer";
import Tree from "sap/m/Tree";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import Default from "niteroi/packages/system/models/Default";
import {Table$RowSelectionChangeEvent} from "sap/ui/table/Table";
import {InputBase$ChangeEvent} from "sap/m/InputBase";

export default class Companies extends BaseController {
    protected currentRoutePattern: string = "";
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();

        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);
            this.currentRoutePattern = route.getPattern();
            if (this.currentRoutePattern === "system/companies" || this.currentRoutePattern === "system/companies/add" || this.currentRoutePattern.includes("system/companies/edit")) {
                route.detachPatternMatched(this.onPatternMatchedHandler);
                route.attachPatternMatched(this.onPatternMatchedHandler);
            }
        }
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        this.resetOriginalModel("company");
        const params = event.getParameters();
        const id = (params.arguments as { id: string }).id ?? "";
        this.getData(id);
    }

    protected getData(objectId: string = ""): void {
        if (objectId) {
            const companyModel = this.createRequestModel();
            companyModel.loadData(`/api/companies/${objectId}`, "", true, "GET", false, true, this.getRequestHeaders()).then(() => {
                this.originalData = Object.assign({}, JSON.parse(companyModel.getJSON()));
                this.setModel(companyModel, "company");
            });
        } else {
            this.setEmptyCompany();
        }
        const companiesModel = this.createRequestModel();
        companiesModel.loadData("/api/companies", "", true, "GET", false, true, this.getRequestHeaders()).then(() => {
            const companiesData = JSON.parse(companiesModel.getJSON()) as ICompany[];
            if (this.currentRoutePattern === "system/companies") {
                const companiesTreeData = this.buildTree(companiesData, (this.getModel("session").getProperty("/user/companyId") as string));
                companiesModel.setData(companiesTreeData);
            } else {
                companiesModel.setData(companiesData);
            }
        }).then(() => {
            this.setModel(companiesModel, "companies");
        });
    }

    private buildTree(companies: ICompany[], companyId: string | null = null): ICompany[] {
        const roots = companies.filter(company =>
            companyId !== null
                ? company.companyId === companyId
                : (company.parentId === null || company.parentId === company.companyId)
        );

        return roots.map(root => ({
            ...root,
            nodes: this.getChildren(companies, root.companyId)
        }));
    }

    private getChildren(companies: ICompany[], parentId: string): ICompany[] | undefined {
        const children = companies
            .filter(company => company.parentId === parentId && company.companyId !== parentId)
            .map(company => {
                const node: ICompany = {...company};
                const childNodes = this.getChildren(companies, company.companyId);
                if (childNodes && childNodes.length > 0) {
                    node.nodes = childNodes;
                }
                return node;
            });

        return children.length > 0 ? children : undefined;
    }

    protected setEmptyCompany(): void {
        this.isModelSaved = true;
        this.originalData = {};
        const emptyCompany = Object.assign({}, Default.initCompany(), {isModelSaved: this.isModelSaved});
        const companyModel = new JSONModel(emptyCompany, true);
        this.setModel(companyModel, "company");

        companyModel.attachPropertyChange(this.setModelEditMode.bind(this));
    }

    public onPressMenuItemAdd(): void {
        this.setEmptyCompany();
        this.navTo("system/companies/add", {}, false);
    }

    public onExit(): void {
        this.isModelSaved = true;
        this.originalData = {};
        if (this.getModel("company")) {
            this.setModel(new JSONModel({}), "company");
        }
    }

    public onPressCompanyItem(event: Table$RowSelectionChangeEvent): void {
        const source = event.getParameter("rowContext");
        if (!source) { // clearing selection
            return;
        }
        const bindingContext = source.getModel();
        const selectedListItem = bindingContext.getProperty(source.getPath());
        this.originalData = selectedListItem;
        this.isModelSaved = true;
        selectedListItem.isModelSaved = this.isModelSaved;
        let model = this.getModel("company");
        if (!model) {
            model = new JSONModel({}, true);
            this.setModel(model, "company");
            model.attachPropertyChange(this.setModelEditMode.bind(this));
        }
        model.setData(selectedListItem);
        this.navTo("system/companies/edit", {"id": selectedListItem.companyId}, false);
    }

    public onPressButtonSaveFormEdit(event: Button$PressEvent): void {
        const page = (((event.getSource().getParent() as Toolbar).getParent() as HeaderContainer).getParent() as Page);
        page.setBusy(true);
        const companyModel = this.getModel("company");
        const companyData = companyModel.getData() as ICompany;
        let isNew = false;
        if (!companyData.companyId) {
            isNew = true;
            companyData.parentId = this.getModel("session").getProperty("/company/companyId") as string;
        }

        if (companyData.companyName && companyData.registrationName && companyData.email && companyData.vatId) {
            const url = "/api/companies";
            const method = isNew ? "POST" : "PUT";
            const headers = this.getRequestHeaders();
            const data = JSON.stringify(companyData);
            companyModel.loadData(url, data, true, method, false, true, headers).then(() => {
                const companyData = companyModel.getData();
                const sessionModel = this.getModel("session");
                if (sessionModel.getProperty("/company/companyId") === companyData.companyId) {
                    sessionModel.setProperty("/company", Object.assign({}, companyData));
                    sessionModel.refresh(true);
                }
                this.navTo("system/companies/edit", {id: companyData.companyId}, false);
                this.showToast(this.getText("msgCompanySavedSuccess"));
            }).catch((response: ILoadDataResponse) => {
                const responseText = JSON.parse(response.responseText);
                this.showToast(this.getText("msgCompanySaveError", [responseText.code, responseText.error]));
            }).finally(() => {
                page.setBusy(false);
            });
        } else {
            page.setBusy(false);
        }
    }

    public onNavButtonPress(): void {
        this.navTo("system/companies", {}, false);
    }

    public onPressButtonDiscardFormEdit(): void {
        this.discardChanges("company");
    }

    public onPressButtonDeleteFormEdit(event: Button$PressEvent): void {
        const page = (((event.getSource().getParent() as Toolbar).getParent() as HeaderContainer).getParent() as Page);
        page.setBusy(true);
        const sessionModel = this.getModel("session");

        const companyModel = this.getModel("company");
        const currentCompanyId = companyModel.getProperty("/companyId") as string;
        if (!currentCompanyId) {
            page.setBusy(false);
            this.showToast(this.getText("msgCompanyCannotDeleteUnsaved"));
            return;
        }

        if (sessionModel.getProperty("/company/companyId") === currentCompanyId) {
            page.setBusy(false);
            this.showToast(this.getText("msgCompanyCannotDeleteCurrent"));
            return;
        }

        const requestModel = this.createRequestModel();
        const url = `/api/companies/${currentCompanyId}`;
        const method = "DELETE";
        const headers = this.getRequestHeaders();
        const params = JSON.stringify({companyId: currentCompanyId});
        requestModel.loadData(url, params, true, method, false, true, headers).then(() => {
            this.showToast(this.getText("msgCompanyDeletedSuccess"));
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            page.setBusy(false);
            this.navTo("system/companies", {}, false);
        });
    }

    public onPressMenuItemSwitchToCompany(event: ListItemBase$PressEvent): void {
        const source = event.getSource();
        const bindingContext = source.getBindingContext("companies");
        const selectedCompany = bindingContext.getModel().getProperty(bindingContext.getPath());
        if (selectedCompany.isActive) {
            this.switchToCompany(selectedCompany.companyId);
        }
    }

    protected switchToCompany(companyId: string): void {
        const sessionModel = this.getModel("session");
        const sessionCompanyId = sessionModel.getProperty("/company/companyId") as string;
        const params = {
            sessionId: sessionCompanyId,
            companyId: companyId
        };
        if (sessionCompanyId !== companyId) {
            const requestModel = this.createRequestModel();
            requestModel.loadData("/api/sessions", JSON.stringify(params), true, "PUT", false, true, this.getRequestHeaders()).then(() => {
                sessionModel.setProperty("/company", Object.assign({}, JSON.parse(requestModel.getJSON()).company));
                sessionModel.setProperty("/components", Object.assign({}, JSON.parse(requestModel.getJSON()).components));
                sessionModel.setProperty("/launchpad", Object.assign({}, JSON.parse(requestModel.getJSON()).launchpad))
                sessionModel.refresh(true);
            });
        }
    }

    public onChangeInput(event: InputBase$ChangeEvent): void {
        const source = event.getSource();
        const value = source.getValue();
        source.setValue(value.replace(/\s+/g, ""));
    }

    public onPressButtonChangeCompanyFormEdit(): void {
        const companyModel = this.getModel("company");
        this.switchToCompany(companyModel.getProperty("/companyId") as string);
    }

    public onPressButtonDeleteLogoFormEdit(): void {
        const companyModel = this.getModel("company");
        companyModel.setProperty("/logo", "");
    }

    public onSearch(event: SearchField$LiveChangeEvent): void {
        this.onSearchList(event, "niteroi-companies-tree-list", ["companyName", "registrationName", "city", "website"]);
    }

    public onExpandAll(): void {
        const tree = this.byId("niteroi-companies-tree-list") as Tree;
        tree.expandToLevel(99);
    }

    public onCollapseAll(): void {
        const tree = this.byId("niteroi-companies-tree-list") as Tree;
        tree.collapseAll();
    }
}
